// src/components/PresentmentDirectoryPanel.js
//
// Record 610 (ruling 259/262). AN OFFICER'S OWN DIRECTORY, on his desk.
//
// Ruling 262: "the GC's directory and the LC's directory each name the person, address and delivery method" of the
// carrier officer who receives a claim, per agreement; "Onboarding: each GC fills his directory; each LC his, on
// taking his seat." Ruling 259: "the office address entered once by the seat holder."
//
//   mode="gc"  the General Chairperson's directory - directories/{committee}. His office address is his committee
//              letterhead's address lines (ruling 229, LetterheadPanel above on his desk); shown here, set there.
//   mode="lc"  the Local Chairperson's directory, one per seat he holds - directories/{committee}/locals/{scopeKey},
//              with his seat's office address (officeAddress), which prints under his signature on a claim he submits.
//
// Ruling 262: "both officers notified". Each panel lists, on its own desk, every place the OTHER directory differs
// from this one, and every directory title the agreement does not name (presentedToCheck.js directoryConflicts).
// Nothing here changes the other officer's record - the GC resolves a difference by correcting his own entry or
// asking the LC to correct his (ruling 13: interpretation is the GC's alone).
//
// firestore.rules (Record 610): directories/{committee} is written by that committee's GC (or the administrator);
// directories/{committee}/locals/{scopeKey} by the LC holding that scope in that committee (or the administrator).
// Both are read by provisioned accounts - the claim form of every party draws on them.
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { doc, getDoc, getDocs, setDoc, collection, serverTimestamp, deleteField } from 'firebase/firestore';   // deleteField: forensic audit 2026-10-16
import { AGREEMENT_LOOKUP } from '../data/agreementData';
import { committeeAgreements, agreementSlug } from '../data/serviceSettings';
import { getClaimRequirements } from '../data/claimRequirements';
import { METHODS, METHOD_KEYS } from '../data/claimPresentment';
import { directoryEntryPatch, entryFor, entryLine, directoryConflicts, officeAddressLines } from '../data/presentedToCheck';

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };
const s = (v) => (v == null ? '' : String(v).trim());
const WHO = { gc: 'General Chairperson', lc: 'Local Chairperson' };

function EntryEditor({ name, slug, cur, designation, onSave, busy }) {
  const [d, setD] = useState(null);
  const e = cur || null;
  const v = d || { name: s(e && e.name), title: s(e && e.title), addressLines: (e && e.addressLines ? e.addressLines : []).join('\n'), method: s(e && e.method), note: s(e && e.note) };
  const set = (k, x) => setD({ ...v, [k]: x });
  return (
    <div style={{ borderTop: '1px solid ' + C.line, padding: '8px 0', fontSize: 12, color: C.text }}>
      <div style={{ fontWeight: 700 }}>{name}</div>
      <div style={{ color: C.dim, margin: '2px 0 6px' }}>The agreement profile names: {designation || 'no designation on file'}</div>
      <div style={{ color: C.dim, marginBottom: 6 }}>Now: {e ? entryLine(e) + (e.addressLines.length ? ' - ' + e.addressLines.join(', ') : '') : 'nobody entered'}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 6 }}>
        <input aria-label="Officer" className="form-input" style={{ fontSize: 12 }} placeholder="Officer's name" value={v.name} onChange={(x) => set('name', x.target.value)} />
        <input aria-label="Title (as the carrier gives it)" className="form-input" style={{ fontSize: 12 }} placeholder="Title (as the carrier gives it)" value={v.title} onChange={(x) => set('title', x.target.value)} />
        <select aria-label="How claims are delivered" className="form-input" style={{ fontSize: 12 }} value={v.method} onChange={(x) => set('method', x.target.value)}>
          <option value="">How claims are delivered…</option>
          {METHODS.filter((m) => m.key !== 'unknown').map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
      </div>
      <textarea aria-label="Address lines" className="form-textarea" rows={3} style={{ fontSize: 12, marginTop: 6, width: '100%' }} placeholder={'Address, one line per row\n(street / suite / city, state ZIP, or e-mail)'} value={v.addressLines} onChange={(x) => set('addressLines', x.target.value)} />
      <input aria-label="Note (optional), e.g. through LCAT; copy to the Superintendent" className="form-input" style={{ fontSize: 12, marginTop: 6 }} placeholder="Note (optional), e.g. through LCAT; copy to the Superintendent" value={v.note} onChange={(x) => set('note', x.target.value)} />
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <button className="btn btn-primary btn-sm" disabled={busy || !d} onClick={async () => { const ok = await onSave(slug, name, v); if (ok) setD(null); }}>Save</button>
        {e ? <button className="btn btn-secondary btn-sm" disabled={busy} onClick={async () => { const ok = await onSave(slug, name, null); if (ok) setD(null); }}>Remove</button> : null}
      </div>
    </div>
  );
}

export default function PresentmentDirectoryPanel({ user, mode }) {
  const committee = s(user && user.committee);
  const lc = mode === 'lc';
  const seats = lc ? (Array.isArray(user && user.scopes) ? user.scopes.filter((k) => s(k) && s(k).indexOf('/') === -1) : []) : [];
  const [seat, setSeat] = useState(seats[0] || '');
  const [open, setOpen] = useState(false);
  const [gcDoc, setGcDoc] = useState(undefined);
  const [locals, setLocals] = useState([]);
  const [letterhead, setLetterhead] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [office, setOffice] = useState(null);
  const [tick, setTick] = useState(0);
  // Forensic audit 2026-10-16: a failed read used to become an empty directory (gcDoc null, locals []), the panel said
  // "Nothing entered yet" / "nobody entered" / no differences, and a save then rewrote the whole `agreements` map from
  // that empty record (and, for an LC, reset his office address to []). Each failure is now named here; while any is
  // set the panel shows it, offers no editor and saves nothing.
  const [readErr, setReadErr] = useState({});
  const [localsRead, setLocalsRead] = useState(false);   // the LC's own record comes from this read: no write before it lands
  // The GC's letterhead is only SHOWN here (his office address); its failure is said where it would show, and blocks
  // nothing this panel writes.
  const [letterheadErr, setLetterheadErr] = useState('');

  useEffect(() => {
    let dead = false;
    if (!committee) return undefined;
    const failed = (k) => (e) => setReadErr((x) => ({ ...x, [k]: ((e && (e.code || e.message)) || 'refused') }));
    setReadErr({}); setLetterheadErr('');
    getDoc(doc(db, 'directories', committee)).then((d) => { if (!dead) setGcDoc(d.exists() ? d.data() : null); }).catch((e) => { if (!dead) failed('the General Chairperson\'s directory')(e); });
    getDocs(collection(db, 'directories', committee, 'locals')).then((q) => { if (!dead) { setLocals(q.docs.map((d) => ({ id: d.id, ...d.data() }))); setLocalsRead(true); } }).catch((e) => { if (!dead) failed('the Local Chairpersons\' directories')(e); });
    if (!lc) getDoc(doc(db, 'committees', committee)).then((d) => { if (!dead) setLetterhead(d.exists() ? (d.data().letterhead || null) : null); }).catch((e) => { if (!dead) setLetterheadErr((e && (e.code || e.message)) || 'refused'); });
    return () => { dead = true; };
  }, [committee, lc, tick]);

  if (!committee || (lc && !seats.length) || (!lc && !(user && (user.role === 'general_chair' || user.isAdmin)))) return null;
  const names = committeeAgreements(AGREEMENT_LOOKUP, committee);
  const designations = {};
  names.forEach((n) => { const r = getClaimRequirements(n); designations[agreementSlug(n)] = s(r && r.presentmentOfficer); });
  const mine = lc ? (locals.find((x) => x.id === seat) || null) : (gcDoc || null);
  const conflicts = directoryConflicts(gcDoc || null, lc ? locals.filter((x) => x.id === seat) : locals, designations);
  const filled = names.filter((n) => entryFor(mine, agreementSlug(n))).length;
  const readFailures = Object.keys(readErr).map((k) => k + ' (' + readErr[k] + ')');
  const readFailed = readFailures.length > 0;
  const ready = !readFailed && gcDoc !== undefined && localsRead;

  // Forensic audit 2026-10-16: THE WRITE TOUCHES ONLY WHAT WAS CHANGED. setDoc with merge deep-merges `agreements`, so
  // one agreement's entry is set (or removed with deleteField) and every other entry stands as the server holds it;
  // officeAddress is written only when it is what was saved. firestore.rules (directories/{committee} and .../locals/
  // {scopeKey}) test the merged document, so the narrower write passes the same rule. On a FIRST write (no document
  // yet - a read that succeeded and found none) the fields the rule requires are supplied: agreements a map, and for
  // an LC officeAddress a list.
  const write = async (agreementsPatch, officeAddress) => {
    if (!ready) { setMsg('Not saved: ' + (readFailed ? 'your directory could not be read, so nothing is written over it. Reload the page.' : 'your directory is still being read.')); return false; }
    setBusy(true); setMsg('');
    try {
      const by = { updatedBy: user.uid, updatedByName: s(user.name || user.email) || 'Unknown', updatedAt: serverTimestamp() };
      const body = { committee, ...by };
      if (agreementsPatch) body.agreements = agreementsPatch;
      else if (!mine) body.agreements = {};
      if (lc) {
        body.scopeKey = seat;
        if (officeAddress !== undefined) body.officeAddress = officeAddress;
        else if (!mine) body.officeAddress = [];
        await setDoc(doc(db, 'directories', committee, 'locals', seat), body, { merge: true });
      } else {
        await setDoc(doc(db, 'directories', committee), body, { merge: true });
      }
      setMsg('Saved.'); setTick((t) => t + 1);
      return true;
    } catch (e) {
      setMsg('Not saved: ' + ((e && (e.code || e.message)) || 'refused') + '. Nothing was changed.');
      return false;
    } finally { setBusy(false); }
  };
  const saveEntry = async (slug, name, input) => {
    const r = directoryEntryPatch(mine, slug, name, input, user, undefined, METHOD_KEYS);
    if (!r.ok) { setMsg(r.errors.join(' ')); return false; }
    return write({ [slug]: input === null ? deleteField() : r.agreements[slug] });
  };
  const saveOffice = async () => {
    const l = officeAddressLines(office || '');
    if (l.length && l.length < 2) { setMsg('An office address is at least two lines (street; city, state ZIP).'); return; }
    const ok = await write(null, l);
    if (ok) setOffice(null);
  };
  const officeNow = lc ? ((mine && Array.isArray(mine.officeAddress)) ? mine.officeAddress : []) : ((letterhead && Array.isArray(letterhead.addressLines)) ? letterhead.addressLines : []);

  return (
    <div style={{ border: '1px solid ' + (conflicts.length || !filled ? C.accent : C.line), background: C.panel, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 14px', gap: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Your directory - who receives claims, and your office address</div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>
            For each agreement: the carrier officer a claim is presented to - name, title, address, how it is delivered. Every claim's "Presented to" is checked against your directory, the {lc ? 'General' : 'Local'} Chairperson's, and the agreement.
            {/* Forensic audit 2026-10-16: no count and no "Nothing entered yet" from a read that failed. */}
            {readFailed
              ? <span style={{ color: C.bad }}> &#9888; Could not read {readFailures.join('; ')}. What is on file is unknown - nothing here can be saved until it can be read (reload the page).</span>
              : <>{' '}{filled ? filled + ' of ' + names.length + ' agreement(s) filled.' : (ready ? 'Nothing entered yet.' : 'Reading…')}
                {conflicts.length ? <span style={{ color: C.accent }}> {conflicts.length} difference(s) to look at.</span> : null}</>}
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => setOpen(!open)}>{open ? 'Hide' : (filled ? 'Open' : 'Fill it in')}</button>
      </div>
      {open ? (
        <div style={{ padding: '0 14px 12px' }}>
          {lc && seats.length > 1 ? (
            <div style={{ fontSize: 12, color: C.text, marginBottom: 8 }}>Seat:{' '}
              <select aria-label="Which seat this entry is for" className="form-input" style={{ width: 'auto', fontSize: 12 }} value={seat} onChange={(e) => { setSeat(e.target.value); setOffice(null); }}>
                {seats.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
          ) : null}

          {conflicts.length ? (
            <div style={{ border: '1px solid ' + C.accent, padding: '8px 10px', marginBottom: 8, fontSize: 12, color: C.accent }}>
              {conflicts.map((c, n) => (
                <div key={n} style={{ marginBottom: 4 }}>
                  {c.kind === 'differ'
                    ? <>&#9888; {c.agreement}{c.scopeKey ? ' (' + c.scopeKey + ')' : ''}: the {WHO.gc}'s directory names {entryLine(c.gc)}; the {WHO.lc}'s names {entryLine(c.lc)} - they differ in {c.differences.join(', ')}.</>
                    : <>&#9888; {c.agreement}{c.scopeKey ? ' (' + c.scopeKey + ')' : ''}: the title "{c.title}" in the {WHO[c.who]}'s directory is not one the agreement names ({designations[c.slug] || 'no designation on file'}). Presentment to the wrong officer can defeat a claim on time.</>}
                </div>
              ))}
              <div style={{ color: C.dim, marginTop: 4 }}>{lc ? 'The General Chairperson resolves a difference; correct your own entry if yours is the one that is wrong.' : 'You resolve a difference: correct your entry, or ask the Local Chairperson to correct his. Nothing here changes his record.'}</div>
            </div>
          ) : null}

          {readFailed ? (
            <div style={{ fontSize: 12, color: C.bad, padding: '8px 0' }}>&#9888; Could not read {readFailures.join('; ')}. This is NOT an empty directory: what is on file is unknown, so no entry, difference or office address is shown and nothing can be saved. Reload the page.</div>
          ) : (<>
          <div style={{ borderTop: '1px solid ' + C.line, padding: '8px 0', fontSize: 12, color: C.text }}>
            <div style={{ fontWeight: 700 }}>Your office address{lc ? ' for this seat' : ''}</div>
            <div style={{ color: C.dim, margin: '2px 0 6px' }}>{(!lc && letterheadErr)
              ? <span style={{ color: C.bad }}>&#9888; Your letterhead could not be read ({letterheadErr}), so your office address is unknown here - not "none on file".</span>
              : officeNow.length ? officeNow.join(', ') : 'None on file - your own profile address prints under your signature until one is entered.'}</div>
            {lc ? (
              office === null
                ? <button className="btn btn-secondary btn-sm" disabled={!ready} onClick={() => setOffice(officeNow.join('\n'))}>{officeNow.length ? 'Change' : 'Enter it'}</button>
                : (
                  <>
                    <textarea aria-label="The office address, one line at a time" className="form-textarea" rows={3} style={{ fontSize: 12, width: '100%' }} placeholder={'Street\nCity, State ZIP'} value={office} onChange={(e) => setOffice(e.target.value)} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      <button className="btn btn-primary btn-sm" disabled={busy} onClick={saveOffice}>Save</button>
                      <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setOffice(null)}>Cancel</button>
                    </div>
                  </>
                )
            ) : <div style={{ color: C.dim }}>It is your letterhead's address - set it in the Letterhead panel on this desk.</div>}
          </div>

          {!ready ? <div style={{ fontSize: 12, color: C.dim }}>Reading…</div> : names.map((n) => (
            <EntryEditor key={(lc ? seat : 'gc') + '|' + n} name={n} slug={agreementSlug(n)} cur={entryFor(mine, agreementSlug(n))}
              designation={designations[agreementSlug(n)]} onSave={saveEntry} busy={busy} />
          ))}
          </>)}
          {msg ? <div style={{ fontSize: 12, color: /^Not saved/.test(msg) ? C.bad : (msg === 'Saved.' ? C.ok : C.accent), marginTop: 6 }}>{msg}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
